import React, { useEffect, useState, useContext } from 'react';
import {
  Box, Container, Typography, Grid, Paper, Stack, Divider, Chip, Button,
  CircularProgress, Alert, IconButton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ConfigLinksDetailsContext } from '../Context/cofingLinksDetailsContext';
import { generateOrderPDF } from '../utils/guestOrderPdf';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BadgeIcon from '@mui/icons-material/Badge';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WorkIcon from '@mui/icons-material/Work';
import TimelineIcon from '@mui/icons-material/Timeline';
import MoneyIcon from '@mui/icons-material/AttachMoney';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import NoteIcon from '@mui/icons-material/Note';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PrintIcon from '@mui/icons-material/Print';
import axios from '../axiosConfig';
import PreviewAttachmentDialog from '../Components/PreviewAttachmentDialog';
import { resolveMediaUrl } from '../utils/mediaUrl';
import GeneralWithCategoriesForm from '../Components/OrderDetailsForm/generalWithCategoriesForm';
import ElectricityForm from '../Components/OrderDetailsForm/electricityForm';
import WaterForm from '../Components/OrderDetailsForm/waterForm';
import ServiceWithDetailsForm from '../Components/OrderDetailsForm/serviceWithDetailsForm';
import CertificateForm from '../Components/OrderDetailsForm/certificateForm';
// ─── Status chip styles ─────────────────────────────────────────────────────
const statusChipProps = {
  1: { color: '#1976d2', bg: '#EEF4FF', border: '#1976d2' },
  2: { color: '#f57c00', bg: '#FFF8E1', border: '#f57c00' },
  3: { color: '#0288d1', bg: '#E1F5FE', border: '#0288d1' },
  4: { color: '#2e7d32', bg: '#E8F5E9', border: '#388e3c' },
  5: { color: '#c62828', bg: '#FFEBEE', border: '#c62828' },
  6: { color: '#6d4c41', bg: '#EFEBE9', border: '#6d4c41' },
};

// ─── Reusable sub-components ─────────────────────────────────────────────────
const SectionHeader = ({ title }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center',
    py: 1.5, px: 3, borderRadius: '8px',
    border: '2px solid', borderColor: 'primary.main',
    width: 'fit-content', minWidth: 200, bgcolor: '#fff',
  }}>
    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
      {title}
    </Typography>
  </Box>
);

const InfoField = ({ icon, label, value, md = 3, ltr = false }) => (
  <Grid item md={md} sm={4} xs={12}>
    <Box display="flex" alignItems="center" gap={1.5}>
      <Box sx={{ color: 'primary.main', display: 'flex', flexShrink: 0 }}>{icon}</Box>
      <Stack direction="row" gap={1} flexWrap="wrap" sx={ltr ? { flex: 1, minWidth: 0 } : {}}>
        <Typography sx={{ fontWeight: 'bold', color: 'text.primary' }}>{label}:</Typography>
        <Typography sx={{ color: 'text.secondary', ...(ltr ? { direction: 'ltr' } : {}) }}>
          {value || '—'}
        </Typography>
      </Stack>
    </Box>
  </Grid>
);

// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatDate = (d, isRTL) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(isRTL ? 'ar-SA' : 'en-GB'); }
  catch { return d; }
};

const GUEST_USER_ID = '00000000-0000-0000-0000-000000000000';

const isGuestAttachment = (attachment) => {
  const insertedById = (attachment?.insertedById || '').toLowerCase();
  return !insertedById || insertedById === GUEST_USER_ID;
};

// ─── Main Component ───────────────────────────────────────────────────────────
const GuestOrderDetails = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar' || i18n.dir?.() === 'rtl';
  const { configLinks } = useContext(ConfigLinksDetailsContext);
  const showEmployeeAttachmentsInOrders = configLinks?.showEmployeeAttachmentsInOrders ?? false;
  const [isPrinting, setIsPrinting] = useState(false);

  const handlePrint = async () => {
    if (isPrinting) return;
    setIsPrinting(true);
    try {
      await generateOrderPDF({
        details,
        result,
        attachments,
        configLinks,
        generalServices,
        certificateData,
        isRTL,
        statusText: {
          1: t('status_new'),
          2: t('status_inProgress'),
          3: t('status_pending'),
          4: t('status_completed'),
          5: t('status_rejected'),
          6: t('status_cancelled'),
        }[result?.status] || '—',
        t,
      });
    } finally {
      setIsPrinting(false);
    }
  };

  // Basic track result passed from GuestPortal
  const result = location.state?.result;

  // Full application details fetched from API
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [generalServices, setGeneralServices] = useState({});
  const [certificateData, setCertificateData] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [isAttachmentsLoading, setIsAttachmentsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [attachmentDetails, setAttachmentsDetails] = useState(undefined);

  const showDialog = (data) => {
    const url = resolveMediaUrl(data.path);
    setAttachmentsDetails({ url, fileName: data.fileName });
    setOpen(true);
  };
  const hideDialog = () => setOpen(false);

  useEffect(() => {
    if (!result?.subscriberServiceId) return;

    const fetchDetails = async () => {
      try {
        setLoading(true);
        setFetchError('');
        const data = await axios.get(`/Guest/Application/${result.subscriberServiceId}`);
        setDetails(data);

        // Fetch sub-form data for services that have specialised fields
        const fragment = data?.fragmentName?.trim().toLowerCase();
        if (['general', 'generalcategories', 'electricity', 'water', 'servicewithdetails'].includes(fragment)) {
          try {
            const gsData = await axios.get(`/Guest/Application/${result.subscriberServiceId}/GeneralServices`);
            setGeneralServices(gsData || {});
          } catch { /* sub-form data optional */ }
        }

        if (fragment === 'certificate' || fragment === 'clearancecertificate') {
          try {
            const certData = await axios.get(`/Guest/Application/${result.subscriberServiceId}/Certificate`);
            setCertificateData(certData || null);
          } catch { /* certificate data optional */ }
        }

        // Fetch attachments
        try {
          setIsAttachmentsLoading(true);
          const attData = await axios.get(`/Guest/Application/${result.subscriberServiceId}/Attachments`);
          const normalizedAttachments = attData || [];
          setAttachments(
            showEmployeeAttachmentsInOrders
              ? normalizedAttachments
              : normalizedAttachments.filter(isGuestAttachment)
          );
        } catch { /* attachments optional */ } finally { setIsAttachmentsLoading(false); }
      } catch (e) {
        console.error('Failed to load full application details', e);
        setFetchError(isRTL ? 'تعذر تحميل تفاصيل الطلب الكاملة.' : 'Could not load full application details.');
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [result?.subscriberServiceId, showEmployeeAttachmentsInOrders]);

  // If no tracking result at all, redirect back
  if (!result) {
    navigate('/guest', { replace: true });
    return null;
  }

  const chip = statusChipProps[result.status] || statusChipProps[1];

  const statusText = {
    1: t('status_new'),
    2: t('status_inProgress'),
    3: t('status_pending'),
    4: t('status_completed'),
    5: t('status_rejected'),
    6: t('status_cancelled'),
  }[result.status] || '—';

  // The canonical data source: full details if loaded, else fall back to track result
  const d = details;

  return (
      <Box sx={{ bgcolor: '#F9FAFC', minHeight: '100vh', py: { xs: 3, md: 5 } }}>
        <Container maxWidth="lg">

          {/* Back + Print buttons */}
          <Stack direction="row" spacing={2} sx={{ mb: 3 }} justifyContent="flex-end" className="no-print">
            <Button
              startIcon={isPrinting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <PrintIcon />}
              onClick={handlePrint}
              disabled={isPrinting}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, bgcolor: '#C62828', '&:hover': { bgcolor: '#B71C1C' }, '&.Mui-disabled': { bgcolor: '#e57373', color: '#fff' } }}
              variant="contained"
            >
              {isPrinting
                ? (isRTL ? 'جاري التحضير...' : 'Preparing...')
                : (isRTL ? 'طباعة / PDF' : 'Print / PDF')
              }
            </Button>
            <Button
              endIcon={isRTL ? <ArrowBackIosNewIcon fontSize="small" color="error" /> : <ArrowForwardIosIcon fontSize="small"  color="error"/>}
              onClick={() => navigate('/guest')}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, color: '#C62828', borderColor: '#C62828' }}
              variant="outlined"
            >
              {isRTL ? 'رجوع' : 'Back'}
            </Button>
          </Stack>

        <div style={{ padding: '0 10px' }}>
          {/* ─── Status banner ─── */}
        <Box sx={{
          bgcolor: '#F5F9FF', border: '1px solid #1976D2',
          borderRadius: '8px', p: 3, mb: 4,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2,
        }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <CheckCircleOutlineIcon sx={{ color: '#1976D2', fontSize: 32 }} />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
              <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '1.2rem' }}>
                {isRTL ? 'رقم الطلب' : 'Request No.'}
              </Typography>
              <Typography fontWeight={900} sx={{ color: '#1976D2', fontSize: '1.3rem', mr: 2 }}>
                {d?.subscriberServiceFullNumber || result.requestNumber}
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="outlined"
            onClick={() => navigate('/guest-request', { state: { service: location.state?.service || {} } })}
            sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 600, borderColor: '#1976D2', color: '#1976D2' }}
            className="no-print"
          >
            {isRTL ? 'طلب جديد' : 'New Request'}
          </Button>
        </Box>

        {/* ─── Loading / error for full details ─── */}
        {loading && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress size={60} />
          </Box>
        )}
        {!loading && fetchError && (
          <Alert severity="warning" sx={{ mb: 3 }}>{fetchError}</Alert>
        )}

        {/* ─── Content: show full details when loaded, otherwise show summary ─── */}
        {!loading && (
          <Stack gap={2}>

            {/* ── 1. معلومات الطلب ── */}
            <SectionHeader title={isRTL ? 'معلومات الطلب' : 'Request Info'} />
            <Paper elevation={2} sx={{ bgcolor: '#fff', p: 3, borderRadius: '12px', border: '1px solid #e0e0e0' }}>
              <Grid container spacing={3}>
                <InfoField
                  icon={<BadgeIcon fontSize="small" />}
                  label={isRTL ? 'رقم الطلب' : 'Request No.'}
                  value={d?.subscriberServiceFullNumber || result.requestNumber}
                />
                <InfoField
                  icon={<DescriptionIcon fontSize="small" />}
                  label={isRTL ? 'نوع الطلب' : 'Service Type'}
                  value={d?.serviceType?.name || result.serviceName}
                />
                <InfoField
                  icon={<CalendarTodayIcon fontSize="small" />}
                  label={isRTL ? 'تاريخ الطلب' : 'Date'}
                  value={formatDate(d?.insertDate || result.insertDate, isRTL)}
                  ltr
                />
                <Grid item md={3} sm={4} xs={12}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <CheckCircleOutlineIcon sx={{ color: 'primary.main', fontSize: 22, flexShrink: 0 }} />
                    <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                      <Typography sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                        {isRTL ? 'الحالة' : 'Status'}:
                      </Typography>
                      <Chip
                        label={statusText}
                        size="small"
                        sx={{ bgcolor: chip.bg, color: chip.color, border: `1.5px solid ${chip.border}`, fontWeight: 700, borderRadius: 2 }}
                      />
                    </Stack>
                  </Box>
                </Grid>
                {(d?.applicationFee != null || result.applicationFee != null) && (
                  <InfoField
                    icon={<MoneyIcon fontSize="small" />}
                    label={isRTL ? 'الرسوم' : 'Fee'}
                    value={`${d?.applicationFee ?? result.applicationFee} ${d?.currencyCode || result.currencyChar3Code || ''}`}
                  />
                )}
              </Grid>
            </Paper>

            <Divider />

            {/* ── 4. عنوان الطلب ── */}
            <SectionHeader title={isRTL ? 'عنوان الطلب' : 'Request Address'} />
            <Paper elevation={2} sx={{ bgcolor: '#fff', p: 3, borderRadius: '12px', border: '1px solid #e0e0e0' }}>
              <Grid container spacing={3}>
                <InfoField
                  icon={<NoteIcon fontSize="small" />}
                  label={isRTL ? 'الغاية من تقديم الطلب' : 'Service Goal'}
                  value={d?.serviceGoal}
                  md={6}
                />
                <InfoField
                  icon={<NoteIcon fontSize="small" />}
                  label={isRTL ? 'ملاحظات المراجعة' : 'Review Notes'}
                  value={d?.serviceReviewNote}
                  md={6}
                />
                {d?.isValidLocation && (
                  <InfoField
                    icon={<LocationOnIcon fontSize="small" />}
                    label={isRTL ? 'الموقع' : 'Location'}
                    value={d.location}
                  />
                )}
                <InfoField
                  icon={<PeopleIcon fontSize="small" />}
                  label={isRTL ? 'نوع المستفيد' : 'Beneficiary Type'}
                  value={d?.beneficiaryType?.name}
                />
                {d?.citizenService?.code && (
                  <InfoField
                    icon={<BadgeIcon fontSize="small" />}
                    label={isRTL ? 'رمز الاشتراك' : 'Subscription Code'}
                    value={d.citizenService.code}
                  />
                )}
              </Grid>
            </Paper>

            <Divider />

            {/* ── 5. مسار الطلب ── */}
            <SectionHeader title={isRTL ? 'مسار الطلب' : 'Request Path'} />
            <Paper elevation={2} sx={{ bgcolor: '#fff', p: 3, borderRadius: '12px', border: '1px solid #e0e0e0' }}>
              <Grid container spacing={3}>
                <InfoField
                  icon={<WorkIcon fontSize="small" />}
                  label={isRTL ? 'المرحلة الحالية' : 'Current Stage'}
                  value={result.stateName}
                />
                <InfoField
                  icon={<TimelineIcon fontSize="small" />}
                  label={isRTL ? 'الإجراء الحالي' : 'Current Activity'}
                  value={d?.activityName || result.activityName}
                />
              </Grid>
            </Paper>

            {/* ── 6. تفاصيل الخدمة المخصصة (كهرباء، ماء، إلخ) ── */}
            {d && (() => {
              const frag = d.fragmentName?.trim().toLowerCase();
              if (frag === 'general' || frag === 'generalcategories')
                return <GeneralWithCategoriesForm isGeneralCategoryForm={frag === 'generalcategories'} generalServices={generalServices} />;
              if (frag === 'electricity') return <ElectricityForm generalServices={generalServices} />;
              if (frag === 'water') return <WaterForm generalServices={generalServices} />;
              if (frag === 'servicewithdetails') return <ServiceWithDetailsForm generalServices={generalServices} />;
              if (frag === 'certificate' || frag === 'clearancecertificate')
                return <CertificateForm externalEntityName={certificateData?.externalEntityName} />;
              return null;
            })()}

            {/* ── 7. المرفقات ── */}
            <Divider />
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, px: 3, borderRadius: '8px', border: '2px solid', borderColor: 'primary.main', width: 'fit-content', minWidth: 200, bgcolor: '#fff' }}>
              <AttachFileIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {isRTL ? 'المرفقات' : 'Attachments'}
              </Typography>
            </Box>
            {isAttachmentsLoading ? (
              <Box sx={{ textAlign: 'center' }}><CircularProgress size={40} /></Box>
            ) : (
              <Paper elevation={2} sx={{ bgcolor: '#fff', p: 3, borderRadius: '12px', border: '1px solid #e0e0e0' }}>
                <Grid container spacing={3}>
                  <Grid item md={3} sm={4} xs={8}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {isRTL ? 'نوع المرفق' : 'Attachment Type'}
                    </Typography>
                  </Grid>
                  <Grid item md={9} xs={4}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {isRTL ? 'استعراض' : 'Preview'}
                    </Typography>
                  </Grid>
                  {attachments?.map((t, index) => {
                    const hasAttachment = t?.fileName && t.path;
                    return (
                      <React.Fragment key={t.attachmentId || index}>
                        <Grid item md={3} xs={8}>
                          <Typography>{`${t.attachmentTypeName}${t?.isMandatory ? ' *' : ''}`}</Typography>
                        </Grid>
                        <Grid item md={9} xs={4}>
                          <IconButton
                            color={hasAttachment ? 'primary' : 'default'}
                            disabled={!hasAttachment}
                            onClick={() => showDialog(t)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Grid>
                      </React.Fragment>
                    );
                  })}
                </Grid>
              </Paper>
            )}

            {open && (
              <PreviewAttachmentDialog
                open={open}
                handleClose={hideDialog}
                attachmentDetails={attachmentDetails}
              />
            )}

          </Stack>
        )}
        </div>
      </Container>
    </Box>
  );
};

export default GuestOrderDetails;
